import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import unifiedMessagingService from '../services/unifiedMessagingService';
import supabaseService from '../services/supabaseService';
import { supabase } from '../utils/supabase';
import ErrorHandler from '../utils/errorHandler';

const MessagingContext = createContext(null);

export const MessagingProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState(null);

  // Derived loading state for backward compatibility
  const isLoading = isLoadingConversations || isLoadingMessages;
  const markingAsReadRef = useRef(new Set()); // Track conversations being marked as read (ref to avoid callback recreation)
  
  // Pagination state for conversations
  const [conversationPagination, setConversationPagination] = useState({
    totalCount: 0,
    hasMore: false,
    currentOffset: 0,
    currentPage: 1,
    pageSize: 6,
    isLoadingMore: false
  });

  // Refs for throttling and deduplication
  const lastConversationLoadRef = useRef(0);
  const conversationLoadThrottleRef = useRef(null);
  const isLoadingRef = useRef(false);
  const latestRequestIdRef = useRef(0); // Track request order to prevent stale responses
  const conversationPaginationRef = useRef(conversationPagination);
  const conversationsLengthRef = useRef(0);
  const selectedConversationRef = useRef(null);
  const CONVERSATION_LOAD_THROTTLE_MS = 2000; // Minimum 2 seconds between loads

  // Keep refs in sync
  useEffect(() => {
    conversationPaginationRef.current = conversationPagination;
  }, [conversationPagination]);
  useEffect(() => {
    conversationsLengthRef.current = conversations.length;
  }, [conversations.length]);
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  /**
   * Load conversations for the current user with pagination support
   * @param {boolean} forceFresh - Force refresh from database (preserves current page)
   * @param {number} direction - Pagination direction (1 for next, -1 for previous, 0 for refresh)
   * @param {boolean} resetPagination - Explicitly reset to page 1 (only for intentional resets)
   */
  const loadConversations = useCallback(async (forceFresh = false, direction = 0, resetPagination = false) => {
    if (!user?.id) return;

    const now = Date.now();
    const pagination = conversationPaginationRef.current;    
    // Generate unique request ID for this load to track response order
    const currentRequestId = ++latestRequestIdRef.current;    
    // If not force fresh and we loaded recently, skip or schedule (don't throttle direction-based loads)
    if (!forceFresh && direction === 0 && now - lastConversationLoadRef.current < CONVERSATION_LOAD_THROTTLE_MS) {
      // Schedule a delayed load if not already scheduled
      if (!conversationLoadThrottleRef.current) {
        conversationLoadThrottleRef.current = setTimeout(() => {
          conversationLoadThrottleRef.current = null;
          loadConversations(false, 0);
        }, CONVERSATION_LOAD_THROTTLE_MS);
      }
      return;
    }
    
    // Prevent concurrent loads
    if (isLoadingRef.current && !forceFresh) {
      return;
    }
    
    // Prevent loading more if already loading more
    if (direction !== 0 && pagination.isLoadingMore) {
      return;
    }

    try {
      isLoadingRef.current = true;
      lastConversationLoadRef.current = now;
      
      if (direction !== 0) {
        setConversationPagination(prev => ({ ...prev, isLoadingMore: true }));
      } else if (conversationsLengthRef.current === 0) {
        // Only show skeleton on initial load, not background refreshes
        setIsLoadingConversations(true);
      }
      setError(null);
      
      // Clear cache if forceFresh is true
      if (forceFresh) {
        unifiedMessagingService.clearConversationCache(user.id);
      }
      
      // Calculate new page and offset based on direction and resetPagination
      let newPage = pagination.currentPage || 1;
      let newOffset = pagination.currentOffset || 0;
      
      if (direction === 1) {
        // Next page
        newPage = (pagination.currentPage || 1) + 1;
        newOffset = (newPage - 1) * pagination.pageSize;
      } else if (direction === -1) {
        // Previous page
        newPage = Math.max(1, (pagination.currentPage || 1) - 1);
        newOffset = (newPage - 1) * pagination.pageSize;
      } else if (resetPagination) {
        // Explicit reset to page 1 (intentional resets only)
        newPage = 1;
        newOffset = 0;
      }
      // else: direction=0 and no resetPagination → preserve current page (forceFresh stays on same page)
      
      if (process.env.NODE_ENV === 'development') {
        // Debug info removed for production
      }
      
      const result = await unifiedMessagingService.getConversations(user.id, {
        limit: pagination.pageSize,
        offset: newOffset,
        skipCache: forceFresh || direction !== 0
      });
      
      if (process.env.NODE_ENV === 'development') {
        // Debug info removed for production
      }
      
      // Guard: only accept responses from the latest request to prevent stale data from overwriting newer results
      if (currentRequestId < latestRequestIdRef.current) {
        return; // Discard stale response
      }
      
      if (result.success) {
        if (process.env.NODE_ENV === 'development') {
          // Debug info removed for production
        }
        
        // Set conversations (replace on direction change, not append)
        setConversations(result.data || []);

        // If the currently selected conversation has a stale/missing conversation_id
        // (can happen after HMR or when data was served from an old Redis cache entry),
        // replace it with the freshly-fetched version so all real-time paths work.
        if (direction === 0) {
          const currentSel = selectedConversationRef.current;
          if (currentSel && !currentSel.conversation_id) {
            // Match by the raw `id` field (the conversation UUID from the RPC)
            const refreshed = (result.data || []).find(
              c => c.id === currentSel.id || c.conversation_id === currentSel.id
            );
            if (refreshed?.conversation_id) {
              setSelectedConversation(refreshed);
            }
          }
        }

        // Update pagination state
        setConversationPagination(prev => ({
          ...prev,
          totalCount: result.totalCount || 0,
          hasMore: result.hasMore || false,
          currentOffset: newOffset,
          currentPage: newPage,
          isLoadingMore: false
        }));
      } else {
        // Error logged via ErrorHandler
        setError(result.error || 'Failed to load conversations');
      }
    } catch (err) {
      // Error logged via ErrorHandler
      setError('Failed to load conversations');
    } finally {
      setIsLoadingConversations(false);
      isLoadingRef.current = false;
      setConversationPagination(prev => ({ ...prev, isLoadingMore: false }));
    }
  }, [user?.id]);

  /**
   * Load more conversations with pagination support
   * @param {number} direction - Pagination direction (1 for next, -1 for previous)
   */
  const loadMoreConversations = useCallback((direction = 1) => {
    const pagination = conversationPaginationRef.current;
    
    // Validate direction
    if (direction !== 1 && direction !== -1) return;
    
    // Check if we can navigate in this direction
    if (direction === 1 && !pagination.hasMore) return; // Can't go next if no more pages
    if (direction === -1 && (pagination.currentPage || 1) <= 1) return; // Can't go previous on first page
    
    if (!pagination.isLoadingMore) {
      loadConversations(false, direction);
    }
  }, [loadConversations]);

  /**
   * Load messages for a specific conversation with enhanced context for merged messages
   */
  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;

    try {
      setIsLoadingMessages(true);
      setError(null);
      
      if (process.env.NODE_ENV === 'development') {
        // Debug info removed for production
      }
      const result = await unifiedMessagingService.getMessages(conversationId);
      if (process.env.NODE_ENV === 'development') {
        // Debug info removed for production
      }
      
      if (result.success) {
        // Job context is already enriched by the service layer (batch query),
        // so we can use the data directly without additional per-message RPC calls
        setMessages(result.data || []);
      } else {
        // Error logged via ErrorHandler
        setError(result.error || 'Failed to load messages');
      }
    } catch (err) {
      // Error logged via ErrorHandler
      setError('Failed to load messages');
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  /**
   * Update conversation's last message
   */
  const updateConversationLastMessage = useCallback((conversationId, lastMessage) => {
    setConversations(prev => 
      prev.map(conv => 
        conv.conversation_id === conversationId
          ? { ...conv, last_message: lastMessage }
          : conv
      )
    );
  }, []);

  /**
   * Send a new message
   */
  const sendMessage = useCallback(async (conversationId, content, attachments = []) => {
    if (!user?.id || !conversationId || !content?.trim()) {
      return { success: false, error: 'Invalid message data' };
    }

    try {
      let recipientId;
      let conversationData = selectedConversation;

      // If we don't have the selected conversation but have a conversationId,
      // fetch the conversation details from the database
      if (!conversationData && conversationId) {
        if (process.env.NODE_ENV === 'development') {
          // Debug info removed for production
        }
        
        const { data: fetchedConversation, error: fetchError } = await supabase
          .from('conversations')
          .select('id, participant_1, participant_2, job_id, last_message_at, created_at')
          .eq('id', conversationId)
          .single();
        
        if (fetchError) {
          // Error logged via ErrorHandler
          return { success: false, error: 'Failed to fetch conversation details' };
        }
        
        // Get the recipient ID and fetch recipient information
        const fetchedRecipientId = fetchedConversation.participant_1 === user.id 
          ? fetchedConversation.participant_2 
          : fetchedConversation.participant_1;
        
        if (process.env.NODE_ENV === 'development') {
          // Debug info removed for production
        }
        const recipientResult = await supabaseService.auth.getUserProfile(fetchedRecipientId);
        if (process.env.NODE_ENV === 'development') {
          // Debug info removed for production
        }
        
        // Transform the conversation to include recipient information
        // Note: supabaseService.auth.getUserProfile returns {profile: data, error: null}
        const recipientProfile = recipientResult.profile;
        conversationData = {
          ...fetchedConversation,
          recipient_id: fetchedRecipientId,
          recipient_name: recipientProfile ? recipientProfile.full_name : 'Unknown User',
          recipient_role: recipientProfile ? recipientProfile.role : 'va',
          recipient_avatar: recipientProfile ? recipientProfile.profile_picture : null,
          company_name: recipientProfile ? recipientProfile.company_name : null,
          last_message: {
            content: 'Start a conversation...',
            timestamp: new Date(),
            sender_id: null
          },
          unread_count: 0
        };
        
        if (process.env.NODE_ENV === 'development') {
          // Debug info removed for production
        }
        
        // Update the conversations list with the enriched conversation data
        setConversations(prev => {
          const existingIndex = prev.findIndex(conv => conv.conversation_id === conversationData.conversation_id);
          if (existingIndex >= 0) {
            // Update existing conversation with recipient info
            const updated = [...prev];
            updated[existingIndex] = { ...updated[existingIndex], ...conversationData };
            return updated;
          } else {
            // Add new conversation to the list
            return [conversationData, ...prev];
          }
        });
        
        // Set as selected conversation so the UI shows the proper recipient info
        setSelectedConversation(conversationData);
        
        if (process.env.NODE_ENV === 'development') {
          // Debug info removed for production
        }
      }

      // Get recipient ID from conversation data
      if (conversationData) {
        recipientId = conversationData.participant_1 === user.id 
          ? conversationData.participant_2 
          : conversationData.participant_1;
      }

      // Validate that we have a valid recipient ID
      if (!recipientId) {
        if (process.env.NODE_ENV === 'development') {
          // Error logged via ErrorHandler
        }
        return { success: false, error: 'No recipient found for this conversation' };
      }

      if (process.env.NODE_ENV === 'development') {
        // Debug info removed for production
      }

      // Determine message type - check if this is an interview message
      let messageType = 'text';
      if (attachments && attachments.length > 0) {
        const firstAttachment = attachments[0];
        if (firstAttachment.type === 'interview') {
          messageType = 'interview_request';
        }
      }

      const result = await unifiedMessagingService.sendMessage(
        conversationId,
        user.id,
        recipientId,
        content.trim(),
        messageType
      );

      if (process.env.NODE_ENV === 'development') {
        // Debug info removed for production
        // Debug info removed for production
      }

      if (result.success) {
        // Add the new message to the current messages
        const newMessage = result.data;
        if (process.env.NODE_ENV === 'development') {
          // Debug info removed for production
        }
        setMessages(prev => {
          if (process.env.NODE_ENV === 'development') {
            // Debug info removed for production
          }
          const updated = [...prev, newMessage];
          if (process.env.NODE_ENV === 'development') {
            // Debug info removed for production
          }
          return updated;
        });
        
        // Update the conversation's last message
        updateConversationLastMessage(conversationId, newMessage);
        
        return { success: true, data: newMessage };
      } else {
        setError(result.error || 'Failed to send message');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Failed to send message';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [user?.id, selectedConversation, updateConversationLastMessage]);

  /**
   * Create a new conversation or get an existing one with improved merge handling
   */
  const createOrGetConversation = useCallback(async (recipientId, jobId = null) => {
    if (!user?.id || !recipientId) {
      return { success: false, error: 'Invalid conversation data' };
    }

    // Check if conversation already exists in local state (for any job context)
    const existingConv = conversations.find(conv => {
      // Handle both data structures - conversations from getConversations and createOrGetConversation
      if (conv.participants && Array.isArray(conv.participants)) {
        // Structure from getConversations with participants array
        return conv.participants.some(p => p.user_id === recipientId);
      } else {
        // Structure from createOrGetConversation with participant_1/participant_2 or recipient_id
        return (conv.recipient_id === recipientId || 
                conv.participant_1 === recipientId || 
                conv.participant_2 === recipientId);
      }
    });

    if (existingConv) {
      setSelectedConversation(existingConv);
      return { success: true, data: existingConv };
    }

    try {
      const result = await unifiedMessagingService.createOrGetConversation(user.id, recipientId, jobId);
      
      if (result.success) {
        const rawConversation = result.data;
        
        // Transform the conversation to match the expected format for the UI
        // We need to get recipient information to populate the conversation properly
        const recipientResult = await supabaseService.auth.getUserProfile(recipientId);
        const recipientProfile = recipientResult.profile;
        
        // Get additional conversation details including merge information
        const { data: conversationDetails } = await supabase
          .from('conversations')
          .select('was_merged, merge_date, merged_from_ids')
          .eq('id', rawConversation.id)
          .single();
        
        // Determine if this conversation was merged
        // Check both the function result and the database record
        const wasMerged = rawConversation.merged || 
                         (conversationDetails && conversationDetails.was_merged) || 
                         false;
        
        const newConversation = {
          conversation_id: rawConversation.id,
          participant_1: rawConversation.participant_1,
          participant_2: rawConversation.participant_2,
          job_id: rawConversation.job_id,
          recipient_id: recipientId,
          recipient_name: recipientProfile ? recipientProfile.full_name : 'Unknown User',
          recipient_role: recipientProfile ? recipientProfile.role : 'va',
          recipient_avatar: recipientProfile ? recipientProfile.profile_picture : null,
          company_name: recipientProfile ? recipientProfile.company_name : null,
          last_message: {
            content: 'Start a conversation...',
            timestamp: new Date(),
            sender_id: null
          },
          unread_count: 0,
          created_at: rawConversation.created_at,
          was_merged: wasMerged,
          merge_date: conversationDetails?.merge_date || null,
          merged_from_ids: conversationDetails?.merged_from_ids || []
        };
        
        // If conversations were merged, we should refresh the conversations list
        // to ensure we don't have stale duplicate conversations in the UI
        if (wasMerged) {
          ErrorHandler.logInfo('MessagingContext', 'Conversations were merged, refreshing conversation list');
          // Delay the refresh slightly to ensure database changes are reflected
          setTimeout(() => {
            loadConversations();
          }, 500);
        } else {
          // Just add the new conversation to the list if no merge occurred
          setConversations(prev => [newConversation, ...prev]);
        }
        
        setSelectedConversation(newConversation);
        return { success: true, data: newConversation };
      } else {
        setError(result.error || 'Failed to create conversation');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Failed to create conversation';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [user?.id, conversations, loadConversations]);

  /**
   * Mark conversation as read
   */
  const markAsRead = useCallback(async (conversationId) => {
    if (!user?.id || !conversationId) return;

    // Prevent duplicate calls for the same conversation
    if (markingAsReadRef.current.has(conversationId)) {
      return;
    }

    // Mark this conversation as being processed
    markingAsReadRef.current.add(conversationId);

    try {
      const result = await unifiedMessagingService.markMessagesAsRead(conversationId, user.id);
      
      if (result.success) {
        // Update local state immediately - this should be sufficient
        setConversations(prev =>
          prev.map(conv =>
            conv.conversation_id === conversationId
              ? { ...conv, unread_count: 0 }
              : conv
          )
        );
      }
    } catch (err) {
      ErrorHandler.logError('MessagingContext', 'markAsRead error', { error: err, conversationId });
      // Silently handle read status update errors
    } finally {
      // Remove from processing set
      markingAsReadRef.current.delete(conversationId);
    }
  }, [user?.id]);

  /**
   * Select a conversation and load its messages
   */
  const selectConversation = useCallback(async (conversation) => {
    // Guard: skip if the same conversation is already selected to avoid
    // unnecessary loadMessages calls that replace the messages array
    const currentId = selectedConversationRef.current?.conversation_id;
    const newId = conversation?.conversation_id;
    if (currentId && newId && currentId === newId) {
      return;
    }

    setSelectedConversation(conversation);
    if (conversation?.conversation_id) {
      await loadMessages(conversation.conversation_id);
      
      // Mark as read in background - don't block conversation selection
      if (conversation.unread_count > 0) {
        markAsRead(conversation.conversation_id);
      }
    }
  }, [loadMessages, markAsRead]);
  
  /**
   * Select a conversation by ID (useful when navigating from other parts of the app)
   */
  const selectConversationById = useCallback(async (conversationId) => {
    if (!conversationId || !user?.id) return false;
    
    try {
      // First check if the conversation is already loaded
      const existingConversation = conversations.find(c => 
        c.conversation_id === conversationId || c.id === conversationId
      );
      
      if (existingConversation) {
        await selectConversation(existingConversation);
        return true;
      }
      
      // If not found in loaded conversations, fetch from database
      const { data: conversation, error } = await supabase
        .from('conversations')
        .select(`
          id, 
          participant_1, 
          participant_2,
          job_id,
          participant_1_profile:user_profiles!conversations_participant_1_fkey(
            id, full_name, company_name, role, profile_picture
          ), 
          participant_2_profile:user_profiles!conversations_participant_2_fkey(
            id, full_name, company_name, role, profile_picture
          )
        `)
        .eq('id', conversationId)
        .single();
        
      if (error || !conversation) {
        // Error logged via ErrorHandler
        return false;
      }
      
      // Create a conversation object compatible with our state
      const isParticipant1 = conversation.participant_1 === user.id;
      const otherParticipant = isParticipant1 
        ? conversation.participant_2_profile 
        : conversation.participant_1_profile;
        
      if (!otherParticipant) {
        // Error logged via ErrorHandler
        return false;
      }
      
      const conversationData = {
        conversation_id: conversation.id,
        participant_name: otherParticipant.full_name,
        participant_avatar: otherParticipant.profile_picture,
        participant_1: conversation.participant_1,
        participant_2: conversation.participant_2,
        unread_count: 0 // We don't know this value but we'll load it from the messages
      };
      
      await loadMessages(conversation.id);
      setSelectedConversation(conversationData);
      return true;
    } catch (err) {
      // Error logged via ErrorHandler
      return false;
    }
  }, [user?.id, conversations, selectConversation, loadMessages]);

  /**
   * Clear current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load conversations when user changes
  // IMPORTANT: Do NOT include loadConversations in dependencies - it would create infinite loop
  // since loadConversations has [user?.id] in its dependencies
  useEffect(() => {
    if (user?.id) {
      loadConversations();
    } else {
      // Clear state when user logs out
      setConversations([]);
      setSelectedConversation(null);
      setMessages([]);
      setError(null);
    }
    
    // Cleanup throttle on unmount or user change
    return () => {
      if (conversationLoadThrottleRef.current) {
        clearTimeout(conversationLoadThrottleRef.current);
        conversationLoadThrottleRef.current = null;
      }
    };
  }, [user?.id]);

  /**
   * Subscribe to incoming messages so the conversation list (unread counts,
   * last-message preview) stays live without a manual refresh.
   * This channel only handles the sidebar — MessageThread manages its own
   * per-conversation channel for the message body.
   */
  useEffect(() => {
    if (!user?.id) return;

    const channelName = `inbox:${user.id}`;

    // Guard against duplicate subscriptions on remounts / React Strict Mode
    const alreadySubscribed = supabase
      .getChannels()
      .some(ch => ch.topic === `realtime:${channelName}`);
    if (alreadySubscribed) return;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          const newMsg = payload.new;
          if (!newMsg) return;
          // Update the matching conversation row in-place for immediate feedback.
          // A background refresh will reconcile the full list afterwards.
          setConversations(prev =>
            prev.map(conv => {
              if (conv.conversation_id !== newMsg.conversation_id) return conv;
              return {
                ...conv,
                last_message: newMsg.content,
                last_message_at: newMsg.sent_at,
                last_sender_id: newMsg.sender_id,
                unread_count: (conv.unread_count || 0) + 1,
              };
            })
          );
          // Background-refresh to get accurate totals from DB (preserves current page)
          loadConversations(true, 0, false);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  /**
   * Send a message with optimistic UI update - message appears instantly
   */
  const sendMessageOptimistic = useCallback(async (conversationId, content, recipientId) => {
    if (!user?.id || !conversationId || !content?.trim() || !recipientId) {
      return { success: false, error: 'Invalid message data' };
    }

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const optimisticMessage = {
      id: tempId,
      _tempId: tempId,
      senderId: user.id,
      recipientId,
      senderName: userProfile?.full_name || user.email || 'You',
      senderAvatar: userProfile?.profile_picture || null,
      content: content.trim(),
      timestamp: new Date().toISOString(),
      type: 'text',
      isRead: false,
      readAt: null,
      _optimistic: true
    };

    // Add message to UI immediately
    setMessages(prev => [...prev, optimisticMessage]);

    // Update conversation's last message immediately
    updateConversationLastMessage(conversationId, optimisticMessage);

    try {
      const result = await unifiedMessagingService.sendMessage(
        conversationId,
        user.id,
        recipientId,
        content.trim(),
        'text'
      );

      if (result.success) {
        // Replace optimistic message with properly formatted real data
        const realMsg = result.data;
        const formattedReal = {
          id: realMsg.id,
          _tempId: tempId,
          senderId: realMsg.sender_id,
          recipientId: realMsg.recipient_id,
          senderName: realMsg.sender?.full_name || userProfile?.full_name || 'You',
          senderAvatar: realMsg.sender?.profile_picture || userProfile?.profile_picture || null,
          content: realMsg.content,
          timestamp: realMsg.sent_at,
          type: realMsg.message_type || 'text',
          isRead: realMsg.is_read || false,
          readAt: realMsg.read_at || null,
          _optimistic: false
        };
        setMessages(prev => prev.map(msg =>
          msg.id === tempId ? formattedReal : msg
        ));
        return { success: true, data: formattedReal };
      } else {
        // Remove optimistic message on failure
        setMessages(prev => prev.filter(msg => msg.id !== tempId));
        const userError = 'Failed to send message';
        setError(userError);
        return { success: false, error: userError };
      }
    } catch (err) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      setError('Failed to send message');
      return { success: false, error: 'Failed to send message' };
    }
  }, [user?.id, user?.email, userProfile?.full_name, userProfile?.profile_picture, updateConversationLastMessage]);

  // Context value with all messaging functionality
  const contextValue = useMemo(() => ({
    // State
    conversations,
    selectedConversation,
    messages,
    isLoading,
    isLoadingConversations,
    isLoadingMessages,
    error,
    
    // Pagination state
    conversationPagination,
    
    // Actions
    loadConversations,
    loadMoreConversations,
    loadMessages,
    sendMessage,
    sendMessageOptimistic,
    createOrGetConversation,
    selectConversation,
    selectConversationById,
    markAsRead,
    clearError,
    setMessages,
    updateConversationLastMessage
  }), [
    conversations,
    selectedConversation,
    messages,
    isLoading,
    isLoadingConversations,
    isLoadingMessages,
    error,
    conversationPagination,
    loadConversations,
    loadMoreConversations,
    loadMessages,
    sendMessage,
    sendMessageOptimistic,
    createOrGetConversation,
    selectConversation,
    selectConversationById,
    markAsRead,
    clearError,
    setMessages,
    updateConversationLastMessage
  ]);

  return (
    <MessagingContext.Provider value={contextValue}>
      {children}
    </MessagingContext.Provider>
  );
};

// Custom hook to use messaging context
export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};
